// Bolting Demonstrations — list + create
// RCSC §7 Pre-Installation Verification (PIV).
// Backend deployed 2026-05-05 (Wave A migration 018); SPA route wired 2026-05-07.
// Create form added 2026-08-05 (CR-2026-WEB-BOLTING-DEMO-CREATE-FORM).
// NOTE: the model stores fastener details in the bolt_assembly_specs JSON column
// (real columns are demonstration_date / audit_type / rcsc_section_7_compliance /
// notes). Lot numbers carry optional Certificate-of-Conformance (CoC) references,
// also stored inside bolt_assembly_specs — no schema change required.

import React, { useEffect, useState, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, Stack, Typography, Breadcrumbs, Paper, Box, Alert,
  TableHead, TableRow, TableCell, TableBody, TableContainer,
  Chip, CircularProgress, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

import { boltingDemonstrationsApi } from "../../utils/boltingDemonstrationsApi";
import { calibrationInstrumentsApi } from "../../utils/calibrationApi";
import PageGuide from "../../components/PageGuide";

import SmartTable from "../../components/SmartTable/SmartTable";
const RESULT_COLORS = { PASS: "success", FAIL: "error", CONDITIONAL: "warning" };
const GRADES = ["A325", "A490", "F3125 GR. A325", "F3125 GR. A490"];

const EMPTY_FORM = {
  lot_number: "", bolt_coc: "", bolt_size: "", grade: "",
  nut_lot: "", nut_coc: "", washer_lot: "", washer_coc: "",
  torque_wrench_id: "", demonstration_date: "",
  assemblies: [
    { test_torque: "", test_result: "PASS" },
    { test_torque: "", test_result: "PASS" },
    { test_torque: "", test_result: "PASS" },
  ],
};

const instLabel = (it) =>
  it.name || it.instrument_id || it.serial_number || it.description || `Instrument #${it.id}`;

const complianceFromResult = (r) =>
  r === "PASS" ? "verified" : r === "FAIL" ? "not_verified" : "partial";

// RCSC §7: a lot passes PIV only if all sampled assemblies pass.
const overallResult = (assemblies) => {
  const rs = assemblies.map((a) => a.test_result);
  if (rs.includes("FAIL")) return "FAIL";
  if (rs.includes("CONDITIONAL")) return "CONDITIONAL";
  return "PASS";
};

export default function BoltingDemonstrations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instruments, setInstruments] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setAssembly = (i, k) => (e) =>
    setForm((f) => ({ ...f, assemblies: f.assemblies.map((a, idx) => (idx === i ? { ...a, [k]: e.target.value } : a)) }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await boltingDemonstrationsApi.list();
      setRows(res?.data?.data || res?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to load bolting demonstrations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    calibrationInstrumentsApi
      .list()
      .then((res) => setInstruments(res?.data?.data || res?.data || []))
      .catch(() => {});
  }, []);

  const openForm = () => { setForm(EMPTY_FORM); setDialogOpen(true); };

  const handleSubmit = async () => {
    if (!form.demonstration_date) {
      toast.error("Demonstration date is required.");
      return;
    }
    if (form.assemblies.some((a) => !String(a.test_torque).trim())) {
      toast.error("RCSC §7 requires a tested torque for all 3 assemblies in the lot.");
      return;
    }
    const overall = overallResult(form.assemblies);
    setSaving(true);
    try {
      const payload = {
        demonstration_date: form.demonstration_date,
        audit_type: "initial",
        rcsc_section_7_compliance: complianceFromResult(overall),
        bolt_assembly_specs: {
          lot_number: form.lot_number,
          bolt_size: form.bolt_size,
          grade: form.grade,
          nut_lot: form.nut_lot,
          washer_lot: form.washer_lot,
          torque_wrench_id: form.torque_wrench_id || null,
          test_assemblies: form.assemblies.map((a, i) => ({ n: i + 1, test_torque: a.test_torque, test_result: a.test_result })),
          test_torque: form.assemblies.map((a) => a.test_torque).filter(Boolean).join(" / "),
          test_result: overall,
          coc: {
            bolt: form.bolt_coc || "",
            nut: form.nut_coc || "",
            washer: form.washer_coc || "",
          },
        },
      };
      await boltingDemonstrationsApi.create(payload);
      toast.success("Bolting demonstration recorded.");
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to record demonstration");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Helmet><title>Bolting Demonstrations | SFM</title></Helmet>
      <Breadcrumbs sx={{ mb: 1 }}>
        <RouterLink to="/dashboard/app" style={{ color: "inherit" }}>Home</RouterLink>
        <Typography color="text.primary">Bolting Demonstrations</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">Bolting Demonstrations (PIV)</Typography>
          <Typography variant="body2" color="text.secondary">
            RCSC §7 Pre-Installation Verification — bolt + nut + washer + torque-wrench traceability per lot.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openForm}>+ New Demonstration</Button>
      </Stack>

      <PageGuide
        why="Prove each fastener lot performs before it goes into the structure — so high-strength bolting is verified, not assumed."
        who="QC inspectors, verifying fastener lots per RCSC pre-installation."
        what="Pre-Installation Verification records: each bolt, nut, and washer lot tested with a calibrated torque wrench to a PASS/FAIL result. Lot numbers may cite an optional Certificate of Conformance (CoC)."
        how="Click + New Demonstration to record one — tie the bolt, nut, and washer lots and the torque wrench to a tested torque value and result."
        when="Before any A325 / A490 / F3125 fastener lot is released to fabrication."
        standard="RCSC §7 · AISC 207-25 §2.6.4"
      />

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <SmartTable tableId="bolting-demonstrations" lockedColumns={["Bolt Lot", "Result"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Bolt Lot</TableCell>
                <TableCell>Size / Grade</TableCell>
                <TableCell>Nut Lot</TableCell>
                <TableCell>Washer Lot</TableCell>
                <TableCell>Torque Wrench</TableCell>
                <TableCell>Test Torque</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>Tested</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  No bolting demonstrations recorded. Add one per RCSC §7 before any A325/A490/F3125 lot is
                  released to fabrication.
                </TableCell></TableRow>
              ) : rows.map((r) => {
                const s = r.bolt_assembly_specs || {};
                const g = (k) => (s[k] != null && s[k] !== "" ? s[k] : r[k]);
                const result = s.test_result || r.test_result;
                const coc = s.coc || {};
                const cocLabel = [coc.bolt, coc.nut, coc.washer].filter(Boolean).join(" · ");
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <strong>{g("lot_number") || "—"}</strong>
                      {cocLabel ? <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>CoC: {cocLabel}</Typography> : null}
                    </TableCell>
                    <TableCell>{(g("bolt_size") || "—")} / {(g("grade") || "—")}</TableCell>
                    <TableCell>{g("nut_lot") || "—"}</TableCell>
                    <TableCell>{g("washer_lot") || "—"}</TableCell>
                    <TableCell>{g("torque_wrench_id") || "—"}</TableCell>
                    <TableCell>{g("test_torque") || "—"}</TableCell>
                    <TableCell>
                      {result
                        ? <Chip label={result} color={RESULT_COLORS[result] || "default"} size="small" />
                        : "—"}
                    </TableCell>
                    <TableCell>{(r.demonstration_date || r.tested_at || "").toString().slice(0, 10) || "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {rows.length} demonstration{rows.length === 1 ? "" : "s"} on file. RCSC §7 + AISC 207-25 §2.6.4 evidence.
      </Typography>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Bolting Demonstration (PIV)</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField label="Bolt Lot No." value={form.lot_number} onChange={setField("lot_number")} fullWidth size="small" />
              <TextField label="Bolt Lot CoC (optional)" value={form.bolt_coc} onChange={setField("bolt_coc")} fullWidth size="small" placeholder="Certificate of Conformance #" />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Size / Diameter" value={form.bolt_size} onChange={setField("bolt_size")} fullWidth size="small" placeholder={'e.g., 3/4"'} />
              <TextField select label="Grade" value={form.grade} onChange={setField("grade")} fullWidth size="small">
                <MenuItem value=""><em>—</em></MenuItem>
                {GRADES.map((gr) => (<MenuItem key={gr} value={gr}>{gr}</MenuItem>))}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Nut Lot" value={form.nut_lot} onChange={setField("nut_lot")} fullWidth size="small" />
              <TextField label="Nut Lot CoC (optional)" value={form.nut_coc} onChange={setField("nut_coc")} fullWidth size="small" placeholder="Certificate of Conformance #" />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Washer Lot" value={form.washer_lot} onChange={setField("washer_lot")} fullWidth size="small" />
              <TextField label="Washer Lot CoC (optional)" value={form.washer_coc} onChange={setField("washer_coc")} fullWidth size="small" placeholder="Certificate of Conformance #" />
            </Stack>
            <TextField
              select
              label="Torque Wrench (calibrated instrument)"
              value={form.torque_wrench_id}
              onChange={setField("torque_wrench_id")}
              fullWidth
              size="small"
              helperText={instruments.length ? "Ties this demo to a specific calibrated wrench." : "No calibrated instruments found — add one under Calibration Register."}
            >
              <MenuItem value=""><em>— none —</em></MenuItem>
              {instruments.map((it) => (<MenuItem key={it.id} value={it.id}>{instLabel(it)}</MenuItem>))}
            </TextField>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Test Assemblies — RCSC §7 (minimum 3 per lot)</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Record the tested torque and result for each of the 3 assemblies sampled from this lot. The lot passes only if all three pass.
              </Typography>
              {form.assemblies.map((a, i) => (
                <Stack key={i} direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 92, fontWeight: 600 }}>Assembly {i + 1}</Typography>
                  <TextField label="Test Torque" value={a.test_torque} onChange={setAssembly(i, "test_torque")} fullWidth size="small" placeholder="e.g., 39 kip" required />
                  <TextField select label="Result" value={a.test_result} onChange={setAssembly(i, "test_result")} sx={{ minWidth: 130 }} size="small">
                    {["PASS", "FAIL", "CONDITIONAL"].map((rv) => (<MenuItem key={rv} value={rv}>{rv}</MenuItem>))}
                  </TextField>
                </Stack>
              ))}
            </Box>
            <TextField label="Demonstration Date" type="date" value={form.demonstration_date} onChange={setField("demonstration_date")} fullWidth size="small" required InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving || !form.demonstration_date || form.assemblies.some((a) => !String(a.test_torque).trim())}>
            {saving ? "Saving…" : "Record Demonstration"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
